/**
 * Shared building blocks for the CT "Health" views migrated into the merchant panel:
 *   - Brand Health  (logged-in merchant's own brand, couriers as columns)
 *   - Courier Health (all couriers)
 *
 * DATA CONTRACT (to wire to the live CT API — endpoint TBD, see useBrandHealth /
 * useCourierHealth). Each "state" is {count, pct}:
 *
 *   CourierHealthRow = {
 *     name, score,                       // score = health % (0-100)
 *     criticalCount, totalCount,         // "X of Y critical"
 *     states: {
 *       breachedPending, firstAttemptOnTime, inQueue,
 *       completedAfterBreached, completedOnTime,
 *     },
 *   }
 *   BrandHealth = { brandName, tier, criticalCount, totalCount, score, couriers: CourierHealthRow[] }
 */
import React from 'react'
import {Box, Typography} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {SUCCESS, ERROR, WARNING, PRIMARY_BLUE, LIGHT_BG} from '../../styles/foundation/colors'

// The five critical states, in display order, each with its colour + bar colour.
export const STATE_META = [
  {key: 'breachedPending', label: 'Breached Pending', color: ERROR},
  {key: 'firstAttemptOnTime', label: 'First Attempt On Time', color: SUCCESS},
  {key: 'inQueue', label: 'In Queue', color: PRIMARY_BLUE},
  {key: 'completedAfterBreached', label: 'Completed After Breached', color: WARNING},
  {key: 'completedOnTime', label: 'Completed On Time', color: SUCCESS},
]

// Health-score colour: green ≥85, amber ≥70, else red.
export const scoreColor = (s) => (s >= 85 ? SUCCESS : s >= 70 ? WARNING : ERROR)

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'))

// Circular health-score ring (the "70" in the screenshot).
export function HealthRing({score, size = 56}) {
  const c = scoreColor(score)
  const stroke = 5
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score || 0))
  return (
    <Box sx={{position: 'relative', width: size, height: size, flexShrink: 0}}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill='none' stroke={LIGHT_BG} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill='none' stroke={c} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={circ - (pct / 100) * circ}
          strokeLinecap='round' transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: SIZE.sm, color: c}}>
          {score != null ? Math.round(score) : '—'}
        </Typography>
      </Box>
    </Box>
  )
}

// Thin stacked composition bar (one segment per state, width ∝ count).
export function StackedBar({states, height = 8}) {
  const total = STATE_META.reduce((s, m) => s + (states?.[m.key]?.count || 0), 0)
  return (
    <Box sx={{display: 'flex', width: '100%', height, borderRadius: '5px', overflow: 'hidden', bgcolor: LIGHT_BG}}>
      {total > 0 && STATE_META.map((m) => {
        const w = ((states?.[m.key]?.count || 0) / total) * 100
        return w > 0 ? <Box key={m.key} sx={{width: `${w}%`, bgcolor: m.color}} /> : null
      })}
    </Box>
  )
}

// A "count (pct%)" cell value.
export function cellText(state) {
  if (!state) return '—'
  return `${fmt(state.count)} (${state.pct != null ? state.pct : 0}%)`
}

export {fmt}

// ── SAMPLE data (layout preview only — replaced by the live CT API once wired) ──
// Mirrors the screenshot so the migrated layout can be reviewed before the API.
const sampleStates = (bp, fa, iq, cab, cot) => ({
  breachedPending: bp, firstAttemptOnTime: fa, inQueue: iq, completedAfterBreached: cab, completedOnTime: cot,
})
export const SAMPLE_COURIERS = [
  {name: 'Delhivery', score: 99.8, criticalCount: 1, totalCount: 506,
    states: sampleStates({count: 1, pct: 0.2}, {count: 398, pct: 78.7}, {count: 106, pct: 20.9}, {count: 0, pct: 0}, {count: 1, pct: 0.2})},
  {name: 'BlueDart', score: 73.2, criticalCount: 6537, totalCount: 24350,
    states: sampleStates({count: 6537, pct: 26.8}, {count: 4132, pct: 17}, {count: 12556, pct: 51.6}, {count: 201, pct: 0.8}, {count: 924, pct: 3.8})},
  {name: 'Ecom Express', score: 65.7, criticalCount: 3072, totalCount: 8965,
    states: sampleStates({count: 3072, pct: 34.3}, {count: 189, pct: 2.1}, {count: 4248, pct: 47.4}, {count: 1456, pct: 16.2}, {count: 0, pct: 0})},
  {name: 'Xpressbees', score: 84.2, criticalCount: 1252, totalCount: 7938,
    states: sampleStates({count: 1252, pct: 15.8}, {count: 89, pct: 1.1}, {count: 6259, pct: 78.8}, {count: 90, pct: 1.1}, {count: 248, pct: 3.1})},
]

// ── SAMPLE expandable Brand-Health matrix (mirrors the deep CT view) ──
// Columns = 4 categories (label TBD from CT). Rows = the 5 states, each with an
// optional `children` sub-breakdown (Breached Pending → ageing; In Queue → courier
// accounts). Every cell is {count, pct}.
const c = (count, pct) => ({count, pct})
export const SAMPLE_BRAND = {
  tier: 'ENTERPRISE',
  criticalCount: 148, totalCount: 3282, score: 95,
  columns: [
    {label: 'Handover Failed', score: 91.5, criticalCount: 5, totalCount: 59},
    {label: 'Delivery', score: 93.9, criticalCount: 127, totalCount: 2090},
    {label: 'Pickup Pending', score: 98.8, criticalCount: 8, totalCount: 664},
    {label: 'RTO', score: 98.3, criticalCount: 8, totalCount: 469},
  ],
  rows: [
    {key: 'breachedPending', label: 'Breached Pending', color: ERROR,
      cells: [c(5, 8.5), c(127, 6.1), c(8, 1.2), c(8, 1.7)],
      children: [
        {label: '0-1 Days', cells: [c(0, 0), c(0, 0), c(0, 0), c(0, 0)]},
        {label: '1-3 Days', cells: [c(0, 0), c(55, 43.3), c(4, 50), c(0, 0)]},
        {label: '3-5 Days', cells: [c(3, 60), c(18, 14.2), c(1, 12.5), c(1, 12.5)]},
        {label: '5-15 Days', cells: [c(2, 40), c(42, 33.1), c(3, 37.5), c(1, 12.5)]},
        {label: '15+ Days', cells: [c(0, 0), c(12, 9.4), c(0, 0), c(6, 75)]},
      ]},
    {key: 'firstAttemptOnTime', label: 'First Attempt On Time', color: SUCCESS,
      cells: [c(8, 13.6), c(216, 10.3), c(5, 0.8), c(39, 8.3)]},
    {key: 'inQueue', label: 'In Queue', color: PRIMARY_BLUE,
      cells: [c(38, 64.4), c(1406, 67.3), c(485, 73), c(391, 83.4)],
      children: [
        {label: 'Blitz SDD', cells: [c(0, 0), c(61, 4.3), c(71, 14.6), c(24, 6.1)]},
        {label: 'Ekart', cells: [c(0, 0), c(13, 0.9), c(2, 0.4), c(16, 4.1)]},
        {label: 'XpressBees 2kg', cells: [c(0, 0), c(6, 0.4), c(0, 0), c(1, 0.3)]},
        {label: 'BD_AirEtailPrepaid', cells: [c(0, 0), c(26, 1.8), c(19, 3.9), c(2, 0.5)]},
        {label: 'ShadowfaxRVPComet', cells: [c(7, 18.4), c(109, 7.8), c(32, 6.6), c(0, 0)]},
        {label: 'BLITZ', cells: [c(0, 0), c(42, 3), c(22, 4.5), c(11, 2.8)]},
        {label: 'Delhivery_EXPRESS', cells: [c(0, 0), c(402, 28.6), c(94, 19.4), c(212, 54.2)]},
        {label: 'Delhivery Reverse', cells: [c(21, 55.3), c(172, 12.2), c(52, 10.7), c(0, 0)]},
        {label: 'XpressBees Reverse NonQC', cells: [c(0, 0), c(2, 0.1), c(0, 0), c(0, 0)]},
        {label: 'BD_AirEtailCOD', cells: [c(0, 0), c(390, 27.7), c(133, 27.4), c(106, 27.1)]},
        {label: 'XpressBeesRVPComet', cells: [c(10, 26.3), c(177, 12.6), c(32, 6.6), c(0, 0)]},
      ]},
    {key: 'completedAfterBreached', label: 'Completed After Breached', color: WARNING,
      cells: [c(0, 0), c(27, 1.3), c(1, 0.2), c(0, 0)]},
    {key: 'completedOnTime', label: 'Completed On Time', color: SUCCESS,
      cells: [c(8, 13.6), c(314, 15), c(165, 24.8), c(31, 6.6)]},
  ],
}

// Build a {stateKey: cell} map for column j, for the column-header composition bar.
export function columnStates(rows, j) {
  const o = {}
  rows.forEach((r) => { o[r.key] = r.cells[j] })
  return o
}

// ── SAMPLE Courier Health (all couriers) — same matrix shape as Brand Health,
// one entity per courier. Scaled clones of SAMPLE_BRAND for layout preview. ──
const scaleCell = (cell, f) => ({count: Math.round(cell.count * f), pct: cell.pct})
const scaleRows = (rows, f) => rows.map((r) => ({
  ...r,
  cells: r.cells.map((x) => scaleCell(x, f)),
  children: r.children ? r.children.map((ch) => ({...ch, cells: ch.cells.map((x) => scaleCell(x, f))})) : undefined,
}))
const courier = (name, score, f) => ({
  name, score,
  criticalCount: Math.round(SAMPLE_BRAND.criticalCount * f),
  totalCount: Math.round(SAMPLE_BRAND.totalCount * f),
  columns: SAMPLE_BRAND.columns.map((col, j) => ({...col, score: [99.8, 73.2, 65.7, 84.2][j] || col.score, criticalCount: Math.round(col.criticalCount * f), totalCount: Math.round(col.totalCount * f)})),
  rows: scaleRows(SAMPLE_BRAND.rows, f),
})
export const SAMPLE_COURIER_MATRIX = [
  courier('Delhivery', 99, 1),
  courier('BlueDart', 73, 0.62),
  courier('Ecom Express', 66, 0.41),
  courier('Xpressbees', 84, 0.5),
]
